import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { academyAPI } from '../../utils/api';

const DIFFICULTY_BADGE = {
  foundation: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  intermediate: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  advanced: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const DifficultyBadge = ({ difficulty }) => (
  <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${DIFFICULTY_BADGE[difficulty] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
    {difficulty || 'topic'}
  </span>
);

const StudyHome = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await academyAPI.getAreas();
        if (active) setAreas(res.data || []);
      } catch (err) {
        if (active) setError('Could not load the curriculum. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Study</h1>
        <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-300">
          Choose a topic to study with your AI tutor and practice quizzes. Educational use only — not legal advice.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-4 rounded-md text-sm">{error}</div>
      )}

      {areas.length === 0 && !error ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No study areas are available yet.</p>
      ) : (
        <div className="space-y-8">
          {areas.map((area) => (
            <section key={area.id}>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{area.name}</h2>
                {area.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{area.description}</p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(area.topics || []).map((topic) => (
                  <Link
                    key={topic.id}
                    to={`/student/study/${topic.id}`}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">{topic.name}</h3>
                      <DifficultyBadge difficulty={topic.difficulty} />
                    </div>
                    {topic.summary && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3">{topic.summary}</p>
                    )}
                    <span className="mt-3 text-xs font-medium text-primary-600 dark:text-primary-400">Study this topic →</span>
                  </Link>
                ))}
                {(area.topics || []).length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500">No topics in this area yet.</p>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudyHome;
